import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import {
  CEMENTO_SECTIONS_ORDER,
  CementoCheckoutState,
  CementoSection,
  TIPO_DESPACHO_SOBRE_CAMION,
  TIPO_ENTREGA_DESPACHO,
  TIPO_ENTREGA_RETIRA_CLIENTE,
} from 'src/app/models/cemento-checkout';
import { OrderSummaryItem } from 'src/app/models/order-summary';
import {
  cementProductNombrePublicacionSelectLabel,
  cementProductActiveDeliveryOption,
  cementProductPriceWithIva,
  type CementProduct,
} from 'src/app/models/cement-product';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StepService } from 'src/app/services/step.service';
import { CementoProductSectionComponent } from './cemento-product-section/cemento-product-section.component';
import { CementoTipoEntregaSectionComponent } from './cemento-tipo-entrega-section/cemento-tipo-entrega-section.component';
import { CementoCantidadServiciosSectionComponent } from './cemento-cantidad-servicios-section/cemento-cantidad-servicios-section.component';
import { CementoFechaServiciosSectionComponent } from './cemento-fecha-servicios-section/cemento-fecha-servicios-section.component';
import { CementoFacturacionSectionComponent } from './cemento-facturacion-section/cemento-facturacion-section.component';
import { CementoCotizacionService } from 'src/app/services/cemento-cotizacion.service';
import { ModalAlertComponent } from 'src/app/shared/modal-alert/modal-alert.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import {
  calculateCementoCheckoutTotal,
  buildCementoQuotePayload,
  mapRecoveredCementoQuoteToState,
  resolveQuantityPalletsForPayload,
} from 'src/app/shared/utils/cemento-cotizacion.mapper';
import { CementoPaymentService } from 'src/app/services/cemento-payment.service';
import { CommonService } from 'src/app/services/common.service';


const SECTION_TITLES: Record<CementoSection, string> = {
  producto: 'Producto',
  tipoEntrega: 'Selecciona cómo quieres recibir tu pedido',
  cantidadServicios: 'Cantidad',
  fechaServicios: 'Fecha de Entrega o Retiro',
  facturacion: 'Datos de facturación',
};

const BACK_LABELS: Record<CementoSection, string> = {
  producto: 'Dirección',
  tipoEntrega: 'Producto',
  cantidadServicios: 'Tipo de entrega',
  fechaServicios: 'Cantidad',
  facturacion: 'Fecha de Entrega o Retiro',
};

type CementoPaymentProvider = 'mercadopago' | 'gnet' | 'flow';

export const DEFAULT_CEMENTO_PAYMENT_PROVIDER: CementoPaymentProvider = 'mercadopago';

export const CEMENTO_PAYMENT_PROVIDERS: ReadonlyArray<{
  id: CementoPaymentProvider;
  name: string;
  description: string;
  logo: string;
}> = [
  {
    id: 'mercadopago',
    name: 'Mercado Pago',
    description: 'Transferencias, tarjetas de crédito y débito',
    logo: 'assets/img/logos/mercadopago.svg',
  },
  {
    id: 'gnet',
    name: 'Getnet',
    description: 'Tarjetas de crédito y débito',
    logo: 'assets/img/logos/getnet-logo.svg',
  },
  {
    id: 'flow',
    name: 'Flow',
    description: 'Tarjetas de crédito y débito',
    logo: 'assets/img/logos/flow.svg',
  },
];

@Component({
  selector: 'app-cemento-producto',
  templateUrl: './cemento-producto.component.html',
  styleUrls: ['./cemento-producto.component.css']
})
export class CementoProductoComponent implements OnInit {
  @ViewChild('paymentModal', { static: true }) paymentModal?: ElementRef;
  @ViewChild(CementoProductSectionComponent) productSection?: CementoProductSectionComponent;
  @ViewChild(CementoTipoEntregaSectionComponent) tipoEntregaSection?: CementoTipoEntregaSectionComponent;
  @ViewChild(CementoCantidadServiciosSectionComponent) cantidadServiciosSection?: CementoCantidadServiciosSectionComponent;
  @ViewChild(CementoFechaServiciosSectionComponent) fechaServiciosSection?: CementoFechaServiciosSectionComponent;
  @ViewChild(CementoFacturacionSectionComponent) facturacionSection?: CementoFacturacionSectionComponent;

  readonly sectionsOrder = CEMENTO_SECTIONS_ORDER;

  /**
   * Secciones donde una selección válida dispara el avance automático sin botón
   * (tanto en primera visita como al cambiar la selección al volver).
   * Solo aplica a secciones con una acción deliberada única (dropdown, datepicker).
   */
  private readonly AUTO_ADVANCE_SECTIONS = new Set<CementoSection>(['producto', 'cantidadServicios', 'fechaServicios']);

  /**
   * Secciones que solo auto-avanzan cuando el usuario CAMBIA su selección
   * respecto a los datos ya guardados. En primera visita muestran el botón
   * Continuar porque el sistema auto-selecciona un valor por defecto.
   */
  private readonly CHANGE_ADVANCE_SECTIONS = new Set<CementoSection>(['tipoEntrega']);

  /**
   * Fingerprint de la selección auto-populada por el sistema en la primera
   * visita a tipoEntrega (sin datos guardados). Permite detectar cuando el
   * usuario cambia desde esa selección inicial para disparar auto-avance.
   * Se resetea cuando el producto cambia (lo que limpia el estado de tipoEntrega).
   */
  private tipoEntregaBaselineFingerprint: string | null = null;

  @Input() readOnly = false;
  readonly paymentProviders = CEMENTO_PAYMENT_PROVIDERS;
  selectedPaymentProvider: CementoPaymentProvider = DEFAULT_CEMENTO_PAYMENT_PROVIDER;
  acceptReadOnlyTerms = false;

  productSectionValid = false;
  tipoEntregaSectionValid = false;
  cantidadServiciosSectionValid = false;
  fechaServiciosSectionValid = false;
  facturacionSectionValid = false;

  private readonly facturacionSummaryDraft$ = new BehaviorSubject<
    Pick<CementoCheckoutState, 'facturacion'> | null
  >(null);

  readonly summaryItems$: Observable<OrderSummaryItem[]>;

  constructor(
    private readonly route: ActivatedRoute,
    public readonly flow: CementoCheckoutFlowService,
    private readonly router: Router,
    private readonly stepService: StepService,
    private readonly quoteService: CementoCotizacionService,
    private readonly paymentService: CementoPaymentService,
    private readonly modalService: NgbModal,
    private readonly spinner: NgxSpinnerService,
    private readonly reCaptchaV3Service: ReCaptchaV3Service,
    private readonly commomService: CommonService,
  ) {
    this.summaryItems$ = combineLatest([
      this.flow.checkoutState,
      this.facturacionSummaryDraft$,
    ]).pipe(
      map(([state, draft]) => buildCementoOrderSummary(state, draft))
    );
  }

  ngOnInit(): void {
    this.stepService.changeStep(2);
    const orderBuy = this.route.snapshot.paramMap.get('id');
    if (orderBuy) {
      this.recoverQuote(orderBuy);
      return;
    }
    if (!this.flow.checkoutStateValue.mapa) {
      this.router.navigateByUrl('/cemento/mapa');
    }
  }

  sectionTitle(section: CementoSection): string {
    return SECTION_TITLES[section];
  }

  backLabel(section: CementoSection): string {
    return BACK_LABELS[section];
  }

  getCanContinue(section: CementoSection): boolean {
    if (this.readOnly) return true;
    if (section === 'producto') return this.productSectionValid;
    if (section === 'tipoEntrega') return this.tipoEntregaSectionValid;
    if (section === 'cantidadServicios') return this.cantidadServiciosSectionValid;
    if (section === 'fechaServicios') return this.fechaServiciosSectionValid;
    if (section === 'facturacion') return this.facturacionSectionValid;
    return true;
  }

  sectionHasSavedData(section: CementoSection): boolean {
    return this.flow.checkoutStateValue[section] != null;
  }

  isAutoAdvanceSection(section: CementoSection): boolean {
    return this.AUTO_ADVANCE_SECTIONS.has(section);
  }

  /**
   * Compara la selección actual del hijo con los datos guardados en el estado.
   * Retorna true si el usuario cambió su selección respecto a lo que ya estaba
   * guardado, lo que habilita el auto-avance incluso al volver a una sección.
   */
  private selectionDiffersFromSaved(section: CementoSection): boolean {
    switch (section) {
      case 'producto': {
        const currentId = this.productSection?.getSelection()?.product?.id;
        const savedId = this.flow.checkoutStateValue.producto?.product?.id;
        return currentId !== savedId;
      }
      case 'cantidadServicios': {
        const currentPallets = this.cantidadServiciosSection?.getSelection()?.pallets;
        const savedPallets = this.flow.checkoutStateValue.cantidadServicios?.pallets;
        return currentPallets !== savedPallets;
      }
      case 'tipoEntrega': {
        const currentSelection = this.tipoEntregaSection?.getSelection();
        return this.hasTipoEntregaChanged(currentSelection);
      }
      default:
        return false;
    }
  }

  onSectionValidChange(section: CementoSection, valid: boolean): void {
    switch (section) {
      case 'producto': this.productSectionValid = valid; break;
      case 'tipoEntrega': this.tipoEntregaSectionValid = valid; break;
      case 'cantidadServicios': this.cantidadServiciosSectionValid = valid; break;
      case 'fechaServicios': this.fechaServiciosSectionValid = valid; break;
      case 'facturacion': this.facturacionSectionValid = valid; break;
    }

    const isActiveSection = this.flow.currentSectionValue === section;
    const isAuto = this.AUTO_ADVANCE_SECTIONS.has(section);
    const isChangeOnly = this.CHANGE_ADVANCE_SECTIONS.has(section);
    const noSavedData = !this.sectionHasSavedData(section);
    const selectionChanged = this.selectionDiffersFromSaved(section);

    const shouldTrigger = isAuto
      ? noSavedData || selectionChanged
      : isChangeOnly && this.sectionHasSavedData(section) && selectionChanged;

    if (valid && isActiveSection && shouldTrigger) {
      setTimeout(() => {
        const stillActive = this.flow.currentSectionValue === section;
        const stillShouldAdvance = isAuto
          ? !this.sectionHasSavedData(section) || this.selectionDiffersFromSaved(section)
          : isChangeOnly && this.sectionHasSavedData(section) && this.selectionDiffersFromSaved(section);
        if (stillActive && stillShouldAdvance) {
          this.onConfirm(section);
        }
      }, 0);
    }
  }

  continueText(): string {
    return this.flow.currentSectionValue === 'facturacion'
      ? 'Continuar al pago'
      : 'Continuar';
  }

  onFacturacionSummaryDraft(
    draft: Pick<CementoCheckoutState, 'facturacion'> | null
  ): void {
    this.facturacionSummaryDraft$.next(draft);
  }

  /**
   * Recibe el fingerprint de selección de tipoEntrega cada vez que cambia.
   * Solo actúa en la primera visita (sin datos guardados): captura la
   * selección auto-populada como línea base y auto-avanza si el usuario
   * cambia a una selección diferente.
   * El escenario de "volver con datos guardados + cambio" está cubierto
   * por onSectionValidChange via hasTipoEntregaChanged.
   */
  onTipoEntregaSelectionChange(fingerprint: string): void {
    if (this.flow.currentSectionValue !== 'tipoEntrega') {
      return;
    }
    if (this.sectionHasSavedData('tipoEntrega') || !fingerprint) {
      return;
    }
    if (this.tipoEntregaBaselineFingerprint === null) {
      this.tipoEntregaBaselineFingerprint = fingerprint;
      return;
    }
    if (fingerprint !== this.tipoEntregaBaselineFingerprint) {
      setTimeout(() => {
        if (
          this.flow.currentSectionValue === 'tipoEntrega' &&
          !this.sectionHasSavedData('tipoEntrega')
        ) {
          this.onConfirm('tipoEntrega');
        }
      }, 0);
    }
  }

  secondaryActionText(section: CementoSection): string | null {
    if (this.readOnly) return null;
    return section === 'facturacion' ? 'Guardar cotización' : null;
  }

  onSecondaryAction(section: CementoSection): void {
    if (section === 'facturacion') {
      this.saveQuote();
    }
  }

  onGoBack(section: CementoSection): void {
    if (section === 'tipoEntrega') {
      this.tipoEntregaSection?.resetSelection();
      this.tipoEntregaSectionValid = false;
      this.tipoEntregaBaselineFingerprint = null;
      this.cantidadServiciosSection?.resetSelection();
      this.cantidadServiciosSectionValid = false;
      this.fechaServiciosSection?.resetSelection();
      this.fechaServiciosSectionValid = false;
      this.facturacionSection?.resetForm();
      this.facturacionSectionValid = false;
      this.flow.clearSectionData('tipoEntrega');
      this.flow.clearSectionData('cantidadServicios');
      this.flow.clearSectionData('fechaServicios');
      this.flow.clearSectionData('facturacion');
      this.onFacturacionSummaryDraft(null);
    }
    if (section === 'cantidadServicios') {
      this.cantidadServiciosSection?.resetSelection();
      this.cantidadServiciosSectionValid = false;
      this.fechaServiciosSection?.resetSelection();
      this.fechaServiciosSectionValid = false;
      this.facturacionSection?.resetForm();
      this.facturacionSectionValid = false;
      this.flow.clearSectionData('cantidadServicios');
      this.flow.clearSectionData('fechaServicios');
      this.flow.clearSectionData('facturacion');
      this.onFacturacionSummaryDraft(null);
    }
    if (section === 'fechaServicios') {
      this.fechaServiciosSection?.resetSelection();
      this.fechaServiciosSectionValid = false;
      this.facturacionSection?.resetForm();
      this.facturacionSectionValid = false;
      this.flow.clearSectionData('fechaServicios');
      this.flow.clearSectionData('facturacion');
      this.onFacturacionSummaryDraft(null);
    }
    if (section === 'facturacion') {
      this.facturacionSection?.resetForm();
      this.facturacionSectionValid = false;
      this.flow.clearSectionData('facturacion');
      this.onFacturacionSummaryDraft(null);
    }
    this.flow.goBack();
  }

  onConfirm(section: CementoSection): void {
    let shouldAdvance = true;
    switch (section) {
      case 'producto': {
        const data = this.productSection?.getSelection();
        if (data) {
          const previousProduct = this.flow.checkoutStateValue.producto?.product;
          const nextProduct = data.product;
          if (previousProduct?.id !== nextProduct?.id) {
            this.resetAfterProductChange();
          }
          this.flow.setSectionData('producto', data);
        } else {
          shouldAdvance = false;
        }
        break;
      }
      case 'tipoEntrega': {
        const tipoData = this.tipoEntregaSection?.getSelection();
        if (tipoData) {
          if (this.hasTipoEntregaChanged(tipoData)) {
            this.resetAfterTipoEntregaChange();
          }
          this.flow.setSectionData('tipoEntrega', tipoData);
        } else {
          shouldAdvance = false;
        }
        break;
      }
      case 'cantidadServicios': {
        const data = this.cantidadServiciosSection?.getSelection();
        if (data) {
          this.flow.setSectionData('cantidadServicios', data);
          this.fechaServiciosSection?.resetSelection();
          this.fechaServiciosSectionValid = false;
          this.facturacionSectionValid = false;
          this.flow.clearSectionData('fechaServicios');
          this.flow.clearSectionData('facturacion');
        } else {
          shouldAdvance = false;
        }
        break;
      }
      case 'fechaServicios': {
        const data = this.fechaServiciosSection?.getSelection();
        if (data) {
          this.flow.setSectionData('fechaServicios', data);
        } else {
          shouldAdvance = false;
        }
        break;
      }
      case 'facturacion': {
        const data = this.facturacionSection?.getSelection();
        if (data) {
          this.flow.setSectionData('facturacion', data);
          this.onFacturacionSummaryDraft(null);
          this.openPaymentSelector();
          shouldAdvance = false;
        } else {
          shouldAdvance = false;
        }
        break;
      }
      default:
        break;
    }

    if (shouldAdvance) {
      this.flow.goNext();
    }
  }

  stepNumber(section: CementoSection): number {
    return this.sectionsOrder.indexOf(section) + 1;
  }

  isLastStep(section: CementoSection): boolean {
    return this.sectionsOrder.indexOf(section) === this.sectionsOrder.length - 1;
  }

  getSummaryText(section: CementoSection): string | null {
    const state = this.flow.checkoutStateValue;
    switch (section) {
      case 'producto': {
        const product = state.producto?.product as CementProduct | undefined;
        if (!product) return null;
        return cementProductNombrePublicacionSelectLabel(product) || product.display_label || null;
      }
      case 'tipoEntrega': {
        if (!state.tipoEntrega?.tipoEntrega) return null;
        return formatTipoEntregaDisplay(state.tipoEntrega);
      }
      case 'cantidadServicios': {
        if (!state.cantidadServicios || !state.producto?.product) return null;
        const pallets = resolveQuantityPalletsForPayload(state.producto.product, state.cantidadServicios);
        if (pallets > 0) return `${pallets} ${pallets === 1 ? 'Pallet' : 'Pallets'}`;
        return null;
      }
      case 'fechaServicios': {
        if (!state.fechaServicios?.schedule?.date) return null;
        return formatDateDdMmYyyy(state.fechaServicios.schedule.date);
      }
      case 'facturacion': {
        return state.facturacion?.billing?.name?.trim() || null;
      }
      default:
        return null;
    }
  }

  private saveQuote(): void {
    const data = this.facturacionSection?.getSelection();
    if (!data) {
      this.facturacionSectionValid = false;
      return;
    }

    this.flow.setSectionData('facturacion', data);
    this.spinner.show();

    this.reCaptchaV3Service.execute('contact').subscribe({
      next: (token: string) => {
        try {
          let payload = buildCementoQuotePayload(this.flow.checkoutStateValue, token);
          const sessionUser = this.commomService.getStorageDataObject('adminUser') as { id?: number } | null;
          if (sessionUser) {
            payload.auth_user_id = sessionUser.id;
          }
          this.quoteService.saveQuote(payload).subscribe({
            next: (response) => {
              this.flow.setOrderBuy(response.order_buy);
              this.spinner.hide();
              this.showModal(
                'cotización enviada',
                'Su cotización de cemento será enviada a su correo.',
                () => {
                  this.finishCementoModalAndGoHome();
                }
              );
            },
            error: () => {
              this.spinner.hide();
              this.showModal(
                'Error',
                'No pudimos guardar su cotización de cemento. Inténtelo nuevamente.'
              );
            },
          });
        } catch {
          this.spinner.hide();
          this.showModal(
            'Error',
            'Faltan datos para guardar la cotización de cemento.'
          );
        }
      },
      error: () => {
        this.spinner.hide();
        this.showModal(
          'Error',
          'No pudimos validar la solicitud. Inténtelo nuevamente.'
        );
      },
    });
  }

  private openPaymentSelector(): void {
    this.selectedPaymentProvider = DEFAULT_CEMENTO_PAYMENT_PROVIDER;
    this.modalService.open(this.paymentModal, {
      size: 'md',
      centered: true,
      windowClass: 'payment-selector-modal',
    });
  }

  payWithFlow(): void {
    this.selectAndStartPayment('flow');
  }

  payWithGetnet(): void {
    this.selectAndStartPayment('gnet');
  }

  payWithMercadoPago(): void {
    this.selectAndStartPayment('mercadopago');
  }

  selectPaymentProvider(provider: CementoPaymentProvider): void {
    this.selectedPaymentProvider = provider;
  }

  selectAndStartPayment(provider: CementoPaymentProvider): void {
    this.selectedPaymentProvider = provider;
    this.startPayment(provider);
  }

  continueReadOnlyPayment(): void {
    if (!this.acceptReadOnlyTerms) {
      return;
    }
    this.startPayment(this.selectedPaymentProvider);
  }

  private startPayment(provider: CementoPaymentProvider): void {
    const data = this.getFacturacionSelection();
    if (!data) {
      this.facturacionSectionValid = false;
      return;
    }

    this.modalService.dismissAll();
    this.flow.setSectionData('facturacion', data);
    this.spinner.show();

    this.reCaptchaV3Service.execute(provider).subscribe({
      next: (token: string) => {
        try {
          const payload = buildCementoQuotePayload(this.flow.checkoutStateValue, token);
          const request = provider === 'flow'
            ? this.paymentService.initFlow(payload)
            : provider === 'gnet'
              ? this.paymentService.initGetnet(payload)
              : this.paymentService.initMercadoPago(payload);

          request.subscribe({
            next: (response) => {
              this.flow.setOrderBuy(response.order_buy);
              this.spinner.hide();
              if (provider === 'flow' && response.token) {
                location.href = `${response.url}?token=${response.token}`;
                return;
              }
              location.href = response.url;
            },
            error: () => {
              this.spinner.hide();
              this.showModal(
                'Error',
                'No pudimos iniciar el pago de cemento. Inténtelo nuevamente.'
              );
            },
          });
        } catch {
          this.spinner.hide();
          this.showModal(
            'Error',
            'Faltan datos para iniciar el pago de cemento.'
          );
        }
      },
      error: () => {
        this.spinner.hide();
        this.showModal(
          'Error',
          'No pudimos validar la solicitud de pago.'
        );
      },
    });
  }

  private recoverQuote(orderBuy: string): void {
    this.readOnly = true;
    this.spinner.show();
    this.quoteService.recoverQuote(orderBuy).subscribe({
      next: (response) => {
        this.flow.replaceState(mapRecoveredCementoQuoteToState(response));
        this.flow.openSection('facturacion');
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.showModal(
          'cotización',
          'Su cotización de cemento se encuentra vencida o es incorrecta.',
          () => {
            this.finishCementoModalAndGoHome();
          }
        );
      },
    });
  }

  private finishCementoModalAndGoHome(): void {
    this.flow.reset();
    this.stepService.changeStep(0);
    void this.router.navigate(['/']);
  }

  private showModal(title: string, description: string, onClose?: () => void): void {
    const modalRef = this.modalService.open(ModalAlertComponent, { centered: true });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.description = description;
    modalRef.componentInstance.headerCloseRunsFunction = true;
    modalRef.componentInstance.function = () => {
      this.modalService.dismissAll();
      if (onClose) {
        onClose();
      }
    };
  }

  private hasTipoEntregaChanged(next: CementoCheckoutState['tipoEntrega']): boolean {
    const current = this.flow.checkoutStateValue.tipoEntrega;
    if (!current) {
      return false;
    }
    return [
      current.tipoEntrega ?? '',
      current.tipoDespacho ?? '',
      current.incoterm ?? '',
      current.comuna?.code ?? '',
    ].join('|') !== [
      next?.tipoEntrega ?? '',
      next?.tipoDespacho ?? '',
      next?.incoterm ?? '',
      next?.comuna?.code ?? '',
    ].join('|');
  }

  private resetAfterProductChange(): void {
    this.tipoEntregaSectionValid = false;
    this.cantidadServiciosSectionValid = false;
    this.fechaServiciosSectionValid = false;
    this.facturacionSectionValid = false;
    this.tipoEntregaBaselineFingerprint = null;
    this.fechaServiciosSection?.resetSelection();
    this.flow.clearSectionData('tipoEntrega');
    this.flow.clearSectionData('cantidadServicios');
    this.flow.clearSectionData('fechaServicios');
    this.flow.clearSectionData('facturacion');
  }

  private resetAfterTipoEntregaChange(): void {
    this.cantidadServiciosSectionValid = false;
    this.fechaServiciosSectionValid = false;
    this.facturacionSectionValid = false;
    this.fechaServiciosSection?.resetSelection();
    this.flow.clearSectionData('cantidadServicios');
    this.flow.clearSectionData('fechaServicios');
    this.flow.clearSectionData('facturacion');
  }

  private getFacturacionSelection(): CementoCheckoutState['facturacion'] | undefined {
    return this.facturacionSection?.getSelection() ?? this.flow.checkoutStateValue.facturacion;
  }

  getReadOnlyProductLabel(): string {
    const product = this.flow.checkoutStateValue.producto?.product as CementProduct | undefined;
    if (!product) {
      return '—';
    }
    return cementProductNombrePublicacionSelectLabel(product) || product.display_label || '—';
  }

  getReadOnlyQuantityLabel(): string {
    const state = this.flow.checkoutStateValue;
    if (!state.cantidadServicios || !state.producto?.product) {
      return '—';
    }
    const pallets = resolveQuantityPalletsForPayload(
      state.producto.product,
      state.cantidadServicios
    );
    return pallets > 0 ? `${pallets} ${pallets === 1 ? 'Pallet' : 'Pallets'}` : '—';
  }

  getReadOnlyTotalLabel(): string {
    const items = buildCementoOrderSummary(this.flow.checkoutStateValue);
    return String(items[5]?.value ?? '—');
  }

  getReadOnlyDispatchAddress(): string {
    const dispatch = this.flow.checkoutStateValue.mapa?.dispatchAddress;
    if (!dispatch) {
      return '—';
    }
    return [dispatch.comuna, dispatch.region].filter(Boolean).join(', ') || '—';
  }

  getReadOnlyDateLabel(): string {
    const date = this.flow.checkoutStateValue.fechaServicios?.schedule?.date;
    return date ? formatDateDdMmYyyy(date) : '—';
  }

  getReadOnlyScheduleRange(): string {
    return this.flow.checkoutStateValue.fechaServicios?.schedule?.schedule_range ?? '';
  }
}

function formatTipoEntregaDisplay(tipo: CementoCheckoutState['tipoEntrega']): string {
  if (!tipo?.tipoEntrega) return '';
  if (tipo.tipoEntrega === TIPO_ENTREGA_RETIRA_CLIENTE && tipo.comuna) {
    return `Retira Cliente - ${tipo.comuna.name}`;
  }
  if (tipo.tipoEntrega === TIPO_ENTREGA_DESPACHO) {
    const subtipo = tipo.tipoDespacho === TIPO_DESPACHO_SOBRE_CAMION ? 'Sobre Camión' : 'A Piso';
    return `Despacho - ${subtipo}`;
  }
  return tipo.tipoEntrega;
}

function formatDateDdMmYyyy(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

const clpFormatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

function buildCementoOrderSummary(
  state: CementoCheckoutState,
  facturacionDraft: Pick<CementoCheckoutState, 'facturacion'> | null = null
): OrderSummaryItem[] {
  const EMPTY_VALUE = '—';

  const items: OrderSummaryItem[] = [
    { label: 'Producto', value: EMPTY_VALUE },
    { label: 'Tipo de entrega', value: EMPTY_VALUE },
    { label: 'Cantidad', value: EMPTY_VALUE },
    { label: 'Fecha de entrega', value: EMPTY_VALUE },
    { label: 'Datos de facturación', value: EMPTY_VALUE },
    { label: 'Monto a pagar', value: EMPTY_VALUE, isTotal: true },
  ];

  if (state.producto?.product) {
    const product = state.producto.product as CementProduct;
    items[0].value =
      cementProductNombrePublicacionSelectLabel(product) ||
      product.display_label;
  }

  if (state.tipoEntrega?.tipoEntrega) {
    items[1].value = formatTipoEntregaDisplay(state.tipoEntrega);
  }

  if (state.cantidadServicios && state.producto?.product) {
    const palletCount = resolveQuantityPalletsForPayload(
      state.producto.product,
      state.cantidadServicios
    );
    if (palletCount > 0) {
      items[2].value = `${palletCount} ${palletCount === 1 ? 'Pallet' : 'Pallets'}`;
    }
  }

  if (state.fechaServicios?.schedule?.date) {
    items[3].value = formatDateDdMmYyyy(state.fechaServicios.schedule.date);
  }

  const billingName = (
    state.facturacion?.billing?.name ??
    facturacionDraft?.facturacion?.billing?.name ??
    ''
  ).trim();
  items[4].value = billingName || EMPTY_VALUE;

  const totalPrice = state.cantidadServicios?.totalPrice;
  if (typeof totalPrice === 'number' && totalPrice > 0) {
    items[5].value = clpFormatter.format(totalPrice);
  } else if (state.cantidadServicios && state.producto?.product) {
    const product = state.producto.product;
    const option = cementProductActiveDeliveryOption(product);
    const price = cementProductPriceWithIva(option) || cementProductPriceWithIva(product);
    const pallets = resolveQuantityPalletsForPayload(product, state.cantidadServicios);
    const recalculated = calculateCementoCheckoutTotal(price, pallets);
    if (recalculated > 0) {
      items[5].value = clpFormatter.format(recalculated);
    }
  }

  return items;
}
